import React, { useState, useEffect, useRef } from 'react';

const Slideshow = ({ 
  slides = [], 
  autoPlay = true, 
  autoPlayInterval = 5000,
  showIndicators = true,
  showArrows = true,
  height = "500px"
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const intervalRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Default slides if none provided
  const defaultSlides = [
    {
      image: "https://via.placeholder.com/1400x600/09244f/ffffff?text=Industrial+Pump+Systems",
      title: "Advanced Industrial Pumping Solutions",
      subtitle: "Reliable performance for demanding applications",
      buttonText: "Explore Products",
      buttonLink: "/products"
    },
    {
      image: "https://via.placeholder.com/1400x600/0f2a5a/ffffff?text=Aurora+Brand+Pumps", 
      title: "Aurora End Suction Pumps",
      subtitle: "Premium quality meets exceptional efficiency",
      buttonText: "Learn More",
      buttonLink: "/products/aurora"
    },
    {
      image: "https://via.placeholder.com/1400x600/1e3a8a/ffffff?text=Water+Management",
      title: "Comprehensive Water Management",
      subtitle: "From municipal to industrial applications",
      buttonText: "Find Your Pump",
      buttonLink: "/pump-selector"
    },
    {
      image: "https://via.placeholder.com/1400x600/064e3b/ffffff?text=Service+Excellence",
      title: "Expert Service & Support",
      subtitle: "Professional maintenance and technical expertise",
      buttonText: "Schedule Service",
      buttonLink: "/service"
    }
  ];

  const slideData = slides.length > 0 ? slides : defaultSlides;

  // Auto-play functionality
  useEffect(() => {
    if (isPlaying && slideData.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => 
          prevIndex === slideData.length - 1 ? 0 : prevIndex + 1
        );
      }, autoPlayInterval);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isPlaying, slideData.length, autoPlayInterval]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? slideData.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === slideData.length - 1 ? 0 : currentIndex + 1);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrevious();
    }
  };

  if (slideData.length === 0) {
    return <div className="slideshow-error">No slides to display</div>;
  }

  const currentSlide = slideData[currentIndex];

  return (
    <div className="hero-slideshow">
      {/* Main slide display */}
      <div 
        className="hero-slide"
        style={{ height }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Background image */}
        <div 
          className="hero-background"
          style={{ backgroundImage: `url(${currentSlide.image})` }}
        />
        
        {/* Overlay gradient */}
        <div className="hero-overlay" />

        {/* Content overlay */}
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">{currentSlide.title}</h1>
            {currentSlide.subtitle && (
              <p className="hero-subtitle">{currentSlide.subtitle}</p>
            )}
          </div>
        </div>

        {/* Button positioned separately */}
        {currentSlide.buttonText && (
          <div className="hero-actions">
            <a href={currentSlide.buttonLink} className="hero-button">
              {currentSlide.buttonText}
            </a>
          </div>
        )}

        {/* Navigation arrows */}
        {showArrows && slideData.length > 1 && (
          <>
            <button 
              className="hero-arrow hero-arrow-left"
              onClick={goToPrevious}
              aria-label="Previous slide"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </button>
            <button 
              className="hero-arrow hero-arrow-right"
              onClick={goToNext}
              aria-label="Next slide"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
              </svg>
            </button>
          </>
        )}

        {/* Play/Pause button */}
        {autoPlay && slideData.length > 1 && (
          <button 
            className="hero-play-pause"
            onClick={togglePlayPause}
            aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Indicators/dots */}
      {showIndicators && slideData.length > 1 && (
        <div className="hero-indicators">
          {slideData.map((_, index) => (
            <button
              key={index}
              className={`hero-indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Slideshow;